import * as yup from 'yup';
export const initialValues = {
	fullname: '',
	contactname: '',
	position: '',
	cnpj: '',
	numEmployees: '',
	plan: '',
	email: '',
	whatsapp: '',
	phone: [''],
	privacy: false,
};

// CNPJ alfanumérico sem máscara: apenas letras/números, até 14 caracteres, sempre maiúsculo.
// Aceita entrada com ou sem máscara (a pontuação é descartada antes do limite de 14).
export const formatCnpj = (value: string): string =>
	value
		.toUpperCase()
		.replace(/[^0-9A-Z]/g, '')
		.slice(0, 14);

// Aplica a máscara visual 00.000.000/0000-00 sobre o valor limpo.
// Apenas para exibição: o valor armazenado/enviado deve ser o de formatCnpj.
export const maskCnpj = (value: string): string => {
	const cnpj = formatCnpj(value);
	let masked = cnpj.slice(0, 2);
	if (cnpj.length > 2) masked += '.' + cnpj.slice(2, 5);
	if (cnpj.length > 5) masked += '.' + cnpj.slice(5, 8);
	if (cnpj.length > 8) masked += '/' + cnpj.slice(8, 12);
	if (cnpj.length > 12) masked += '-' + cnpj.slice(12, 14);
	return masked;
};

// Calcula um dígito verificador do CNPJ a partir da base informada.
// O valor de cada caractere é (código ASCII - 48), conforme a Nota Técnica
// da Receita Federal para o CNPJ alfanumérico: dígitos '0'-'9' resultam em 0-9
// e letras 'A'-'Z' resultam em 17-42. Isso mantém compatibilidade com CNPJs
// numéricos legados, já que para eles o valor coincide com o próprio dígito.
function calcCNPJCheckDigit(base: string): number {
	const tamanho = base.length;
	let soma = 0;
	let pos = tamanho - 7;
	for (let i = tamanho; i >= 1; i--) {
		soma += (base.charCodeAt(tamanho - i) - 48) * pos--;
		if (pos < 2) {
			pos = 9;
		}
	}
	return soma % 11 < 2 ? 0 : 11 - (soma % 11);
}

export function checkCNPJ(cnpjArg) {
	if (cnpjArg == null || cnpjArg == '') {
		return false;
	}
	// CNPJ alfanumérico (sem máscara): 12 caracteres alfanuméricos (raiz + ordem)
	// seguidos de 2 dígitos verificadores sempre numéricos.
	const cnpj = String(cnpjArg)
		.toUpperCase()
		.replace(/[^0-9A-Z]/g, '');

	if (cnpj.length != 14) {
		return false;
	}

	// Elimina CNPJs com todos os caracteres iguais (inválidos conhecidos)
	if (/^(.)\1{13}$/.test(cnpj)) {
		return false;
	}

	const base = cnpj.substring(0, 12);
	const digitos = cnpj.substring(12);

	// Os dois dígitos verificadores são sempre numéricos
	if (!/^[0-9]{2}$/.test(digitos)) {
		return false;
	}

	const dv1 = calcCNPJCheckDigit(base);
	if (dv1 != Number(digitos.charAt(0))) {
		return false;
	}

	const dv2 = calcCNPJCheckDigit(base + digitos.charAt(0));
	if (dv2 != Number(digitos.charAt(1))) {
		return false;
	}

	return true;
}

// Domínios de e-mail pessoal recusados no cadastro de empresa. A MESMA lista existe no
// fluxo do Node-RED (POST /company/register, nó "validation"): mudou aqui, tem que mudar lá.
export const BLOCKED_EMAIL_DOMAINS = [
	'gmail.com',
	'googlemail.com',
	'yahoo.com',
	'yahoo.com.br',
	'hotmail.com',
	'hotmail.com.br',
	'live.com',
	'live.com.br',
	'outlook.com',
	'msn.com',
	'ibest.com.br',
	'bol.com.br',
	'uol.com.br',
	'ig.com.br',
	'terra.com.br',
	'globo.com',
	'zipmail.com.br',
];

export const PERSONAL_EMAIL_MESSAGE =
	'Use um e-mail corporativo da empresa. Não são permitidos e-mails pessoais (ex.: Gmail, Yahoo, Hotmail).';

/**
 * Só responde true quando o domínio é reconhecidamente pessoal. Entrada incompleta
 * (ainda sendo digitada, sem "@") passa, para não acusar erro no meio da digitação.
 */
export const isPersonalEmail = (value?: string | null): boolean => {
	if (!value || !value.includes('@')) {
		return false;
	}
	const domain = value.split('@')[1]?.trim().toLowerCase() ?? '';
	return BLOCKED_EMAIL_DOMAINS.includes(domain);
};

const schema = yup.object().shape({
	fullname: yup.string().required('Nome da Empresa é obrigatório!'),
	contactname: yup.string().required('Nome do Contato é obrigatório!'),
	position: yup.string().required('Nome do Cargo é obrigatório!'),
	cnpj: yup
		.string()
		.matches(/^[0-9A-Za-z]{14}$/, { message: 'CNPJ inválido!', excludeEmptyString: true })
		.required('CNPJ é obrigatório!')
		.test('cnpj', 'CNPJ inválido!', checkCNPJ),
	email: yup
		.string()
		.email('Email deve ter um formato válido!')
		.required('Email é obrigatório!')
		.test('corporate-email', PERSONAL_EMAIL_MESSAGE, value => !isPersonalEmail(value)),
	whatsapp: yup
		.string()
		.required('WhatsApp é obrigatório!')
		.matches(/([0-9]{4,5}-[0-9]{4})/, { message: 'WhatsApp inválido!', excludeEmptyString: true })
		.test('testEmpty', 'WhatsApp é obrigatório!', val => {
			if (!val || val === '' || val === '(__) ____-____' || val === '(__) _____-____') {
				return false;
			}
			return true;
		}),
	phone: yup
		.array()
		.of(
			yup
				.string()
				.test('phone-format', 'Telefone inválido!', function(value) {
					// Se estiver vazio ou for máscara vazia, não valida (campo opcional)
					if (!value || value === '' || value === '(__) ____-____' || value === '(__) _____-____') {
						return true;
					}
					// Se tiver valor, valida o formato
					return /([0-9]{4,5}-[0-9]{4})/.test(value);
				}),
		)
		.test('testEmpty', 'Telefone inválido!', val => {
			if (!val || val.length === 0) {
				return true; // Telefone é opcional - array vazio é válido
			}
			// Verifica se todos os telefones estão vazios ou são máscaras vazias
			const allEmpty = val.every(phone => 
				!phone || phone === '' || phone === '(__) ____-____' || phone === '(__) _____-____'
			);
			if (allEmpty) {
				return true; // Todos vazios = válido (opcional)
			}
			// Se tem algum preenchido, verifica se é válido
			let hasOneValid = false;
			val.forEach(phone => {
				if (phone != null && !['', '(__) ____-____', '(__) _____-____'].includes(phone)) {
					hasOneValid = true;
				}
			});
			return hasOneValid; // Se tem algum preenchido, precisa ser válido
		}),
	numEmployees: yup.string().required('Quantidade de funcionários é obrigatório!'),
	plan: yup.string().required('Plano é obrigatório!'),
	privacy: yup.boolean().isTrue('Você precisa concordar com a política de privacidade!'),
});

export default schema;
